import React, { Component, ReactNode } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  Typography,
  Card,
  CardContent,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';

interface CalendarErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  retryCount: number;
}

interface CalendarErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

export class CalendarErrorBoundary extends Component<
  CalendarErrorBoundaryProps,
  CalendarErrorBoundaryState
> {
  private resetTimeoutId: number | null = null;

  constructor(props: CalendarErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<CalendarErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo,
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error for debugging
    console.error('Calendar Error Boundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: CalendarErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset error state if any props have changed (when resetOnPropsChange is true)
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0,
    });
  };

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState(
        (prevState) => ({
          hasError: false,
          error: null,
          errorInfo: null,
          showDetails: false,
          retryCount: prevState.retryCount + 1,
        })
      );
    }
  };

  handleToggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    const { children, fallback, maxRetries = 3 } = this.props;
    const { hasError, error, errorInfo, showDetails, retryCount } = this.state;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const canRetry = retryCount < maxRetries;
      const errorMessage = error?.message || 'An unexpected error occurred';
      const isNetworkError = errorMessage.toLowerCase().includes('network') ||
                            errorMessage.toLowerCase().includes('fetch');
      const isChunkError = errorMessage.toLowerCase().includes('chunk') ||
                          errorMessage.toLowerCase().includes('loading');

      return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
          <Card>
            <CardContent>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                icon={<BugReportIcon />}
              >
                <AlertTitle>
                  {isNetworkError ? 'Network Error' : 
                   isChunkError ? 'Loading Error' : 
                   'Calendar Error'}
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {isNetworkError 
                    ? 'Unable to connect to the server. Please check your internet connection.'
                    : isChunkError
                    ? 'Failed to load calendar components. This might be due to a recent update.'
                    : 'The calendar encountered an unexpected error and needs to be reloaded.'
                  }
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  {canRetry && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={this.handleRetry}
                    >
                      Try Again ({maxRetries - retryCount} attempts left)
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.location.reload()}
                  >
                    Reload Page
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={this.handleToggleDetails}
                  >
                    {showDetails ? 'Hide' : 'Show'} Details
                  </Button>
                </Stack>
              </Alert>

              <Collapse in={showDetails}>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Error Details:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="pre" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mb: 2,
                      }}
                    >
                      {error?.stack || errorMessage}
                    </Typography>

                    {errorInfo && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          Component Stack:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          component="pre" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {errorInfo.componentStack}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Collapse>

              {!canRetry && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Maximum retry attempts reached. Please reload the page or contact support if the problem persists.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return children;
  }
}

export default CalendarErrorBoundary;